// The rating ask: once per player, a moment after a game's results open, how are you liking it? It
// asks about whichever mode gets there first and never comes back in the other. A thumbs up or down
// can add a note, which reaches the feedback inbox marked with both. The server counts the ask as
// made the moment it opens, so closing it ends it too.
import { useEffect, useRef, useState, type FormEvent } from 'react'
import type { FeedbackRequest, Rating, RatingAsk as Ask, RatingMode, RatingRequest, Thumbs } from '@wbwl/contract'
import { Icon } from './Icon.tsx'
import { Sheet, useToast } from './shell.tsx'
import { api } from '../lib/api.ts'

/** How long the results show before the ask opens. */
const ASK_AFTER_MS = 1600

const NAME: Record<RatingMode, string> = { daily: 'Letters on Fire', slow_burn: 'Slow Burn' }

/**
 * `finished`: these results, of `edition`, opened as its game ended here; nothing is asked otherwise.
 * If another sheet is open (`held`) when the moment comes, it doesn't open, and the next game asks instead.
 */
export function RatingAsk({ mode, edition, finished, held = false }: { mode: RatingMode; edition: number; finished: boolean; held?: boolean }) {
  const [open, setOpen] = useState(false)
  const [thumbs, setThumbs] = useState<Thumbs | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const heldRef = useRef(held)
  heldRef.current = held

  useEffect(() => {
    if (!finished) return
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const start = Date.now()
    api.get<Ask>(`/me/rating/${mode}`).then((a) => {
      if (!live || !a.ask) return
      timer = setTimeout(() => {
        if (heldRef.current) return
        setOpen(true)
        api.post(`/me/rating/${mode}`, { rating: null } satisfies RatingRequest).catch(() => {})
      }, Math.max(0, ASK_AFTER_MS - (Date.now() - start)))
    }, () => {})
    return () => { live = false; clearTimeout(timer) }
  }, [finished, mode])

  function close() {
    setOpen(false)
    if (thumbs) toast(<><Icon name="check" />Thanks for letting us know</>)
  }

  async function answer(rating: Rating) {
    setBusy(true)
    try {
      await api.post(`/me/rating/${mode}`, { rating } satisfies RatingRequest)
      if (rating === 'none') {
        setOpen(false)
        toast('No problem. We won’t ask again.')
      } else {
        setThumbs(rating)
      }
    } catch {
      toast('Couldn’t send that. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function send(e: FormEvent) {
    e.preventDefault()
    if (!thumbs || !note.trim()) return
    setBusy(true)
    try {
      await api.post('/feedback', { body: note.trim(), mode, rating: thumbs, edition } satisfies FeedbackRequest)
      setOpen(false)
      toast(<><Icon name="check" />Thanks — we read every note</>)
    } catch {
      toast('Couldn’t send that. Try again later.')
    } finally {
      setBusy(false)
    }
  }

  const head = (title: string) => (
    <div className="share-head">
      <h2>{title}</h2>
      <button type="button" className="ibtn boxed" aria-label="Close" onClick={close}><Icon name="close" /></button>
    </div>
  )

  return (
    <Sheet open={open} onClose={close} label={`How are you liking ${NAME[mode]}?`}>
      {thumbs === null ? (
        <div className="share rate">
          {head(`How are you liking ${NAME[mode]}?`)}
          <div className="rate-picks">
            <button type="button" className="rate-pick up" disabled={busy} onClick={() => void answer('up')}><Icon name="thumbup" />Liking it</button>
            <button type="button" className="rate-pick down" disabled={busy} onClick={() => void answer('down')}><Icon name="thumbdown" />Not really</button>
          </div>
          <button type="button" className="btn plain rate-skip" disabled={busy} onClick={() => void answer('none')}>Prefer not to say</button>
        </div>
      ) : (
        <form className="share feedback rate" onSubmit={send}>
          {head(thumbs === 'up' ? 'Glad to hear it!' : 'Thanks for being honest')}
          <p className="muted rate-sub">Want to tell us more? It’s up to you.</p>
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={4} aria-label="Your note"
            placeholder={thumbs === 'up' ? 'What do you like? What would make it even better?' : 'What isn’t working for you? Rules, words, bugs, anything.'}
          />
          <button className="btn ink block" type="submit" disabled={busy || !note.trim()}>Send</button>
          <button type="button" className="btn plain rate-skip" onClick={close}>No thanks</button>
        </form>
      )}
    </Sheet>
  )
}
